import React, { createContext, useContext, useEffect, useState } from 'react';
import { adminApi } from '../config/axios';

const PackagesContext = createContext({});

const DEFAULT_PERIODS = [
  { name: 'one_month',  percentage: 0, period: 1,  price: 0 },
  { name: 'six_months', percentage: 0, period: 6,  price: 0 },
  { name: 'one_year',   percentage: 0, period: 12, price: 0 },
];

/**
 * Offline fallback for the gated feature flags, used only when GET
 * /admin/features fails or returns empty. The backend's config/features.php is
 * the canonical list; this must stay in sync with it.
 *
 * It is a FALLBACK, not the source: Step2 renders from `gatedFeatures` and the
 * wizard submits from the same array, so a flag missing from this list is still
 * handled correctly whenever the fetch succeeds.
 */
const GATED_FEATURES_FALLBACK = [
  { key: 'birthday_mode',      label: 'Birthday Mode' },
  { key: 'happy_hour',         label: 'Happy Hour' },
  { key: 'spin_and_win',       label: 'Spin & Win' },
  { key: 'layouts',            label: 'Layouts' },
  { key: 'clock',              label: 'Clock Widget' },
  { key: 'weather',            label: 'Weather Widget' },
  { key: 'rss_feed',           label: 'RSS Feeds (+ Custom RSS)' },
  { key: 'prayer_app',         label: 'Prayer App' },
  { key: 'animated_layouts',   label: 'Animated Layouts' },
  { key: 'live_meeting_access', label: 'Broadcast / Live Meeting' },
  { key: 'remote_control',     label: 'Remote Control' },
  { key: 'video_wall',         label: 'Video Wall (+ Templates, Groups, Content Library)' },
  { key: 'scheduling_pro',     label: 'Scheduling Pro (copy, date-range, seasonal)' },
  { key: 'locations',          label: 'Locations (multi-branch)' },
  { key: 'ai_menu_ocr',        label: 'AI Menu Import (OCR)' },
  { key: 'ai_dish_photos',     label: 'AI Dish Photos' },
  { key: 'ai_promo_designer',  label: 'AI Promo Designer' },
  { key: 'hardware_control',   label: 'Hardware Control' },
  { key: 'match_scheduler',    label: 'Match Day Scheduler' },
  { key: 'smart_ad_scheduler', label: 'Smart Ad Scheduler' },
  { key: 'ad_marketplace',     label: 'Ad Marketplace' },
  { key: 'crm_customers',      label: 'Customer Directory (CRM)' },
  { key: 'marketing_email',    label: 'Email Marketing' },
  { key: 'marketing_whatsapp', label: 'WhatsApp Marketing' },
  { key: 'marketing_sms',      label: 'SMS Marketing' },
  { key: 'qr_loyalty',         label: 'QR Loyalty & Ordering' },
  { key: 'venue_overlay_promos', label: 'عروضي (Venue Overlay Promo Rotation)' },
  // UI-only visibility flags (build 6A): no backend gate, but part of the catalogue, so a
  // package created while GET /admin/features is unreachable still states them.
  { key: 'music',              label: 'Music' },
  { key: 'create',             label: 'Create' },
  { key: 'content',            label: 'Content' },
  { key: 'programs',           label: 'Programs' },
  { key: 'schedule',           label: 'Schedule' },
  { key: 'suggestions',        label: 'Suggestions' },
  { key: 'quick_show',         label: 'Quick Show' },
  { key: 'youtube',            label: 'YouTube' },
  { key: 'cafe_wizard',        label: 'Cafe Wizard' },
  { key: 'theme_schedule',     label: 'Theme Schedule' },
];

export function PackagesProvider({ children }) {
  const [data, setData]                         = useState([]);
  const [selectedPackage, setSelectedPackage]   = useState({});
  const [selectedFeature, setSelectedFeature]   = useState([]);
  const [selectedMediaTypes, setSelectedMediaTypes] = useState([]);
  const [selectedLayers, setSelectedLayers]     = useState([]);
  const [selectedPeriods, setSelectedPeriods]   = useState(DEFAULT_PERIODS);
  const [periodPrices, setPeriodPrices]         = useState({ one_month: 0, six_months: 0, one_year: 0 });

  // The canonical gated-flag list, fetched once and shared by BOTH the Step 2
  // checkboxes and the wizard's submit payload.
  //
  // It lives here rather than in Step2 because those two used to disagree:
  // Step2 rendered from the fetched list while Wizard submitted a hand-written
  // mirror of it, and the submitted object replaces packages.features wholesale.
  // Any flag added to config/features.php but not to that mirror was silently
  // dropped on every save even with its box ticked — which is exactly what
  // happened to qr_loyalty (2026-08-15: ticking "QR Loyalty & Ordering" saved
  // with no error and the toggle came back off). One shared array means the set
  // that is rendered is by construction the set that is submitted.
  const [gatedFeatures, setGatedFeatures] = useState(GATED_FEATURES_FALLBACK);

  useEffect(() => {
    let active = true;
    adminApi.get('/features')
      .then((res) => {
        const list = res?.data?.data?.features;
        if (active && Array.isArray(list) && list.length > 0) {
          setGatedFeatures(list.filter(f => f && f.key));
        }
      })
      .catch(() => { /* keep fallback */ });
    return () => { active = false; };
  }, []);

  const resetWizard = () => {
    setSelectedFeature([]);
    setSelectedMediaTypes([]);
    setSelectedLayers([]);
    setSelectedPeriods(DEFAULT_PERIODS);
    setPeriodPrices({ one_month: 0, six_months: 0, one_year: 0 });
  };

  return (
    <PackagesContext.Provider value={{
      data, setData,
      selectedPackage, setSelectedPackage,
      selectedFeature, setSelectedFeature,
      selectedMediaTypes, setSelectedMediaTypes,
      selectedLayers, setSelectedLayers,
      selectedPeriods, setSelectedPeriods,
      periodPrices, setPeriodPrices,
      gatedFeatures,
      defaultPeriods: DEFAULT_PERIODS,
      resetWizard,
    }}>
      {children}
    </PackagesContext.Provider>
  );
}

export const usePackages = () => useContext(PackagesContext);
